import React, { createContext, useContext } from "react";
import Navbar, { NavbarNavigation } from "./components/shared/navbar";
import Footer from "./components/shared/footer";
import {
    createBrowserRouter,
    isRouteErrorResponse,
    Navigate,
    Outlet,
    ScrollRestoration,
    useParams,
    useRouteError,
} from "react-router-dom";
import { RouterProvider } from "react-router-dom";
import LoginPage from "./pages/login/login-page";
import { AuthProvider, RequireAuth } from "./auth/auth-provider";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import OrgPage from "./pages/org/org-page";
import { OrgProvider, RequireOrg } from "./auth/org-provider";
import CreateOrgPage from "./pages/create-org/create-org-page";
import InvitePage from "./pages/invite/invite-page";
import { ArrowRight, BotIcon, Laptop, LayoutTemplateIcon, PlugIcon, Smartphone, SproutIcon } from "lucide-react";
import { ReactComponent as TekkrScriptLogoDark } from "./static/tekkr-logo-script-dark.svg";
import { Button } from "./components/ui/button";
import { ReactComponent as TekkrLogoWhite } from "./static/tekkr-logo-white.svg";
import NoOrgPage from "./pages/no-org/no-org-page";
import { defaultQueryClient } from "./lib/query-client";
import { ContentSpacer } from "./pages/common/layout";
import { InternalErrorPageContent, NotFoundErrorPageContent } from "./components/shared/error-message";
import { useColorScheme } from "./lib/color-scheme";
import { TooltipProvider } from "./components/ui/tooltip";
import { OrgSetupPage } from "./pages/org-setup/org-setup-page";
import { cn } from "./lib/utils";
import * as Sentry from "@sentry/react";
import { ParallaxProvider } from "react-scroll-parallax";
import { OnboardingWelcomePage } from "./pages/onboarding/onboarding-welcome-page";
import { FlagsmithProvider } from "./components/shared/providers/flagsmith";
import { CreateProjectChatPage } from "./pages/create-project-chat/create-project-chat-page";
import { ProjectPage } from "./pages/project/project-page";
import { ProjectTimelineChatPage } from "./pages/project-timeilne-chat/project-timeline-chat-page";
import { ProjectHistoryPage } from "./pages/project-history/project-history-page";
import { ProjectMeetingsPage } from "./pages/project-meetings/project-meetings-page";
import { ProjectUpdatesPage } from "./pages/project-updates/project-updates-page";
import { ProjectsListPage } from "./pages/project-list/project-list-page";
import { GlobalDialogsProvider } from "./components/modals/global-dialogs";
import { ConfirmationDialogProvider } from "./components/modals/confirmation-dialog-provider";
import { ProjectProvider } from "./data/project";
import { ProjectChat } from "./pages/project/components/project-chat";
import { AgentsPage } from "./pages/agents/agents-page";
import { TemplatesPage } from "./pages/templates/templates-page";
import { TemplatePreviewPage } from "./pages/templates/template-preview-page";
import { ProjectSettingsPage } from "./pages/project/project-settings-page";
import { IntegrationsPage } from "./pages/integrations/integrations-page";
import { GoogleDriveSettingsPage } from "./pages/settings/google-drive-settings-page";
import { useIsDemoOrg } from "./lib/use-is-demo-org";
import AdminPage from "./pages/prototypes/AdminPage";
import ViewerPage from "./pages/prototypes/ViewerPage";
import {
    GoogleDriveTopBanner,
    useGoogleDriveBannerVisible,
} from "./components/shared/google-drive/google-drive-top-banner";

const AppPageUseNavContext = createContext<(() => NavbarNavigation) | undefined>(undefined);

export function AppPage(props: React.PropsWithChildren) {
    const useNav = useContext(AppPageUseNavContext);
    if (!useNav) {
        throw new Error("Can't use AppPage without nav context");
    }
    const nav = useNav();
    const bannerVisible = useGoogleDriveBannerVisible();
    return (
        <div className={"flex min-h-screen flex-col"}>
            <ScrollRestoration />
            <GoogleDriveTopBanner />
            <Navbar navigation={nav} bannerOffset={bannerVisible}></Navbar>
            <main className="flex flex-1 flex-col">{props.children}</main>
            <Footer></Footer>
        </div>
    );
}

export function BaseApp(props: { router: typeof router; navProviderHook: () => NavbarNavigation }) {
    useColorScheme();
    const [queryClient] = React.useState(() => defaultQueryClient);

    return (
        <>
            <div
                className={cn(
                    "fixed bottom-0 left-0 right-0 top-0 bg-background p-8",
                    "z-50 flex flex-col items-center justify-center gap-6 sm:hidden"
                )}
            >
                <TekkrScriptLogoDark className={"fixed top-4 z-50 hidden dark:block"} />
                <div className={"flex flex-row items-center gap-4"}>
                    <Smartphone className={"h-8 w-8"} />
                    <ArrowRight />
                    <Laptop className={"h-8 w-8"} />
                </div>
                <h3 className={"text-center"}>We don't have a mobile version of Tekkr (yet) 😢</h3>
                <p className={"text-center"}>
                    We're very sorry for the inconvenience, in the meantime you can visit our website to learn more
                    about Tekkr or use Tekkr on your computer.
                </p>
                <a href={"https://tekkr.io"}>
                    <Button>
                        <TekkrLogoWhite className={"me-2 h-6 w-6"} />
                        Learn more about Tekkr
                    </Button>
                </a>
            </div>
            <div className={"hidden sm:block"}>
                <FlagsmithProvider>
                    <ParallaxProvider>
                        <TooltipProvider>
                            <QueryClientProvider client={queryClient}>
                                <ReactQueryDevtools initialIsOpen={false} />
                                <AuthProvider>
                                    <AppPageUseNavProvider navProviderHook={props.navProviderHook}>
                                        <RouterProvider router={props.router} />
                                    </AppPageUseNavProvider>
                                </AuthProvider>
                            </QueryClientProvider>
                        </TooltipProvider>
                    </ParallaxProvider>
                </FlagsmithProvider>
            </div>
        </>
    );
}

export function AppPageUseNavProvider(props: React.PropsWithChildren<{ navProviderHook: () => NavbarNavigation }>) {
    return (
        <AppPageUseNavContext.Provider value={props.navProviderHook}>{props.children}</AppPageUseNavContext.Provider>
    );
}

export function ErrorBoundary() {
    const error = useRouteError();

    if (isRouteErrorResponse(error) && error.status === 404) {
        return (
            <ContentSpacer>
                <NotFoundErrorPageContent />
            </ContentSpacer>
        );
    }

    Sentry.captureException(error);

    return (
        <ContentSpacer>
            <InternalErrorPageContent />
        </ContentSpacer>
    );
}

const router = createBrowserRouter([
    {
        path: "/",
        element: (
            <RequireAuth>
                <OrgProvider>
                    <RequireOrg>
                        <GlobalDialogsProvider>
                            <ConfirmationDialogProvider>
                                <AppPage>
                                    <Outlet />
                                </AppPage>
                            </ConfirmationDialogProvider>
                        </GlobalDialogsProvider>
                    </RequireOrg>
                </OrgProvider>
            </RequireAuth>
        ),
        children: [
            {
                path: "/",
                element: <Navigate to="/projects" />,
            },
            {
                path: "/new-project",
                element: <CreateProjectChatPage />,
            },
            {
                path: "/project/:id",
                element: <ProjectRoute />,
                children: [
                    {
                        path: "",
                        element: <ProjectPage />,
                    },
                    {
                        path: "revise",
                        element: <ReviseProjectPage />,
                    },
                    {
                        path: "timeline",
                        element: <ProjectTimelineChatPage />,
                    },
                    {
                        path: "updates",
                        element: <ProjectUpdatesPage />,
                    },
                    {
                        path: "history",
                        element: <ProjectHistoryPage />,
                    },
                    {
                        path: "meetings",
                        element: <ProjectMeetingsPage />,
                    },
                    {
                        path: "settings",
                        element: <ProjectSettingsPage />,
                    },
                ],
            },
            {
                path: "/projects",
                element: <ProjectsListPage />,
            },
            {
                path: "/agents",
                element: <AgentsPage />,
            },
            {
                path: "/templates",
                element: <TemplatesPage />,
            },
            {
                path: "/templates/:templateId",
                element: <TemplatePreviewPage />,
            },
            {
                path: "/integrations",
                element: <IntegrationsPage />,
            },
            {
                path: "/settings",
                element: <Navigate to="/settings/google-drive" />,
            },
            {
                path: "/settings/google-drive",
                element: <GoogleDriveSettingsPage />,
            },
            {
                path: "/org/:orgId",
                element: <OrgPage />,
            },
        ],
        errorElement: (
            <RequireAuth>
                <OrgProvider>
                    <RequireOrg>
                        <AppPage>
                            <ErrorBoundary />
                        </AppPage>
                    </RequireOrg>
                </OrgProvider>
            </RequireAuth>
        ),
    },
    {
        path: "/invite/:inviteToken",
        element: (
            <RequireAuth>
                <InvitePage></InvitePage>
            </RequireAuth>
        ),
    },
    {
        path: "/create-organization",
        element: (
            <OrgProvider>
                <CreateOrgPage></CreateOrgPage>
            </OrgProvider>
        ),
    },
    {
        path: "/organization-setup",
        element: (
            <OrgProvider>
                <OrgSetupPage></OrgSetupPage>
            </OrgProvider>
        ),
    },
    {
        path: "/onboarding/welcome",
        element: (
            <OrgProvider>
                <OnboardingWelcomePage />
            </OrgProvider>
        ),
    },
    {
        path: "/no-organization",
        element: <NoOrgPage></NoOrgPage>,
    },
    {
        path: "/login",
        element: <LoginPage></LoginPage>,
    },
    // Prototype publishing routes (no Firebase auth required)
    {
        path: "/prototypes/admin",
        element: <AdminPage />,
    },
    {
        path: "/prototypes/v/:slug",
        element: <ViewerPage />,
    },
]);

function ProjectRoute() {
    const { id } = useParams();
    if (!id) {
        throw new Error("Project ID is required");
    }
    return (
        <ProjectProvider projectId={id}>
            <ProjectChat>
                <Outlet />
            </ProjectChat>
        </ProjectProvider>
    );
}

function ReviseProjectPage() {
    const params = useParams();
    const projectId = params.id;
    return <CreateProjectChatPage reviseProjectId={projectId} />;
}

function useNavigation(): NavbarNavigation {
    const isDemoOrg = useIsDemoOrg();
    return {
        logoLink: "/projects",
        primaryItems: [
            {
                icon: SproutIcon,
                path: "/projects",
                title: "Projects",
                tooltipText: "Ongoing Projects",
            },
            {
                icon: BotIcon,
                path: "/agents",
                title: "Agents",
                tooltipText: "Custom AI Agents",
            },
            {
                icon: LayoutTemplateIcon,
                path: "/templates",
                title: "Templates",
                tooltipText: "Project Templates",
            },
            ...(isDemoOrg
                ? [
                      {
                          icon: PlugIcon,
                          path: "/integrations",
                          title: "Integrations",
                          tooltipText: "Connect your tools",
                      },
                  ]
                : []),
        ],
    };
}

export function ProjectsApp() {
    return <BaseApp router={router} navProviderHook={useNavigation} />;
}
